/**
 * The tick boxes behind the quote generator, and the mapping from what someone ticked onto the
 * pricing model that already exists in `estimate-project.ts`.
 *
 * This file is the single place option labels live. The quiz renders from it, the API route
 * validates against it, and the quote email prints from it, so the wording a person ticked and the
 * wording that lands in their inbox cannot drift apart. Adding an option here is enough to make it
 * appear in all three.
 *
 * Nothing here prices anything. `estimate-project.ts` owns the numbers; this only translates.
 */

import type {
  EstimatorBookingPayments,
  EstimatorComplexity,
  EstimatorEcommerce,
  EstimatorIntegrations,
  EstimatorWebsiteTier,
  ProjectEstimatorInputs,
} from "@/lib/project-estimator-types";

/** What they want built. More than one is normal and is the common case for bigger projects. */
export type QuoteBuildType = "website" | "ecommerce" | "system" | "app";

export const BUILD_TYPES: { id: QuoteBuildType; label: string; detail: string }[] = [
  {
    id: "website",
    label: "A website",
    detail: "Your shop window: pages that explain what you do and turn visitors into enquiries.",
  },
  {
    id: "ecommerce",
    label: "An online shop",
    detail: "Sell products online, with your own branded storefront and checkout.",
  },
  {
    id: "system",
    label: "A business system",
    detail: "Internal software: workflows, job tracking, dashboards, the admin side of things.",
  },
  {
    id: "app",
    label: "An app",
    detail: "A product your customers or your team use, on phones or in a browser.",
  },
];

/**
 * WHAT THE SITE NEEDS TO DO. This replaced a page-count band on 2026-09-06.
 *
 * Tom: "Price on what the site DOES, not page count." A page count asks a business owner to
 * guess a number they do not know, and answers a question nobody is really asking; it is also
 * the one dimension a £400 freelancer can match exactly. What the site DOES is the thing they
 * can actually answer, and it is the thing that separates the rungs.
 *
 * `detail` is shown under each option because the words alone are not self-evident: "sells" has
 * to mean something specific to somebody choosing between three boxes.
 */
export const WEBSITE_TIERS: {
  id: EstimatorWebsiteTier;
  label: string;
  detail: string;
}[] = [
  {
    id: "presence",
    label: "Get found and contacted",
    detail:
      "A clean, fast site that says who you are, what you do and where you work, with an easy way to get in touch.",
  },
  {
    id: "sells",
    label: "Bring in enquiries",
    detail:
      "Built to be found and to convert: a page for each thing you sell, search structure that works, and content you can change yourself.",
  },
  {
    id: "transacts",
    label: "Take bookings or payments",
    detail:
      "Customers book or pay through an established service. Provider fees are separate. Accounts, shops and custom workflows add to the build cost.",
  },
];

export const SPEC_CLARITY: { id: EstimatorComplexity; label: string; detail: string }[] = [
  {
    id: "straightforward",
    label: "I know exactly what I want",
    detail: "It is written down, or you can point at something similar.",
  },
  {
    id: "moderate",
    label: "I have a rough idea",
    detail: "You know the shape of it, the detail needs working out together.",
  },
  {
    id: "demanding",
    label: "I am still figuring it out",
    detail: "You know the problem better than the solution. That is a fine place to start.",
  },
];

/**
 * What they are actually trying to fix.
 *
 * Asked first, and asked as a tap rather than a text box so it costs nothing to answer. Two reasons
 * it earns its place: the estimate email can open by reflecting the problem back instead of leading
 * with a parts list, and a rep reading the lead knows why this person came before they read what
 * they ticked.
 */
export type QuoteGoal =
  | "no_website"
  | "website_not_working"
  | "manual_admin"
  | "losing_leads"
  | "new_product"
  | "other";

export const QUOTE_GOALS: { id: QuoteGoal; label: string; reflect: string }[] = [
  {
    id: "no_website",
    label: "We have no website, or it is badly out of date",
    reflect: "getting a site up that actually represents the business",
  },
  {
    id: "website_not_working",
    label: "We have a website but it does not bring in work",
    reflect: "turning your website into something that brings work in",
  },
  {
    id: "manual_admin",
    label: "Too much of the job is manual admin",
    reflect: "taking the manual admin out of how the business runs",
  },
  {
    id: "losing_leads",
    label: "Enquiries are slipping through the cracks",
    reflect: "making sure enquiries stop slipping through",
  },
  {
    id: "new_product",
    label: "We want to launch something new",
    reflect: "getting your new product built and launched",
  },
  {
    id: "other",
    label: "Something else",
    reflect: "solving the problem you came to us with",
  },
];

/**
 * Ongoing maintenance, which is a monthly retainer and not a one-off cost.
 *
 * CANONICAL SOURCE IS LEAD ENGINE, NOT THIS FILE. These three tiers are locked to real recurring
 * Stripe subscription links in `lead-engine/src/lib/staff-retainer-links.ts`, and the tier-to-offer
 * mapping is the Starter / Growth / Operator product definition in
 * `lead-engine/src/lib/staff-hub-content.ts`. Changing a price here without changing it there means
 * the site quotes one figure and the payment link charges another. If they ever need to diverge,
 * fetch them instead of editing this list.
 *
 * Deliberately NOT the estimator's `SUPPORT_ADD`, which models support as a one-off lump added to
 * the project total (up to £17,000). That is not the offer. Ownly builds the thing once and then
 * charges monthly to look after it, so the quote has to show two numbers of two different kinds.
 */
export type RetainerTier = "starter" | "growth" | "operator" | "platform" | "largePlatform";

/** Cheapest first. Declared once so a page cannot list them in a different order. */
export const RETAINER_ORDER: readonly RetainerTier[] = ["starter", "growth", "operator", "platform", "largePlatform"];

export const RETAINER_TIERS: Record<
  RetainerTier,
  { monthlyGbp: number; label: string; detail: string }
> = {
  starter: {
    monthlyGbp: 150,
    label: "Starter",
    detail: "Hosting, updates, security and a person to call when something looks wrong.",
  },
  growth: {
    monthlyGbp: 250,
    label: "Growth",
    detail:
      "Everything in Starter, plus keeping the booking and enquiry journey working as you change things.",
  },
  operator: {
    monthlyGbp: 500,
    label: "Operator",
    detail:
      "Everything in Growth, plus looking after the system your team relies on day to day, and the integrations behind it.",
  },
  /* The two plans above Operator, added 2026-09-21 with lead-engine's care plans (Tom: "think we
     need more monthly care options"). A platform a whole team uses every day, with storage growing
     behind it, costs more to keep alive than a small internal tool, and was being looked after for
     the same £500. Existing clients keep the price they are on. */
  platform: {
    monthlyGbp: 750,
    label: "Platform",
    detail:
      "For software several kinds of user are in every day, where storage keeps growing and downtime stops work.",
  },
  largePlatform: {
    monthlyGbp: 1200,
    label: "Large platform",
    detail:
      "Heavy daily use with payments running through it, and priority support when something breaks.",
  },
};

/**
 * Feature tick boxes. Each one is a plain boolean, because a tick box that secretly means three
 * different price tiers is how a quote ends up wrong. The two "deep" variants below are separate
 * ticks that only appear once their parent is ticked.
 */
export type QuoteFeatureKey =
  | "userAccounts"
  | "adminDashboard"
  | "bookingPayments"
  | "bookingPaymentsAdvanced"
  | "integrations"
  | "integrationsHeavy"
  | "largeCatalogue";

export const FEATURES: {
  id: QuoteFeatureKey;
  label: string;
  detail: string;
  /** Only offered when this other tick is on. Keeps the first screen short. */
  requires?: QuoteFeatureKey;
  /** Only offered when one of these build types is ticked. */
  buildTypes?: QuoteBuildType[];
}[] = [
  {
    id: "userAccounts",
    label: "Customer logins and accounts",
    detail: "People sign in, see their own information, and pick up where they left off.",
  },
  {
    id: "adminDashboard",
    label: "An admin area for your team",
    detail: "Somewhere your staff manage what customers see and track what is going on.",
  },
  {
    id: "bookingPayments",
    label: "Online bookings or payments",
    detail: "Set up a proven booking service or payment link so customers can book or pay online.",
  },
  {
    id: "bookingPaymentsAdvanced",
    label: "Subscriptions, invoicing, or a full checkout",
    detail: "Recurring billing and the finance side, not just a one-off payment.",
    requires: "bookingPayments",
  },
  {
    id: "integrations",
    label: "Connects to software you already use",
    detail: "Your accounting package, your CRM, your booking system, whatever you already run on.",
  },
  {
    id: "integrationsHeavy",
    label: "Lots of systems, kept in sync",
    detail: "Several tools talking to each other continuously, not a one-way export.",
    requires: "integrations",
  },
  {
    id: "largeCatalogue",
    label: "A large or existing product catalogue",
    detail: "Hundreds of products, or a catalogue to move across from somewhere else.",
    buildTypes: ["ecommerce"],
  },
];

export const APP_PLATFORMS: { id: "ios" | "android" | "web"; label: string }[] = [
  { id: "ios", label: "iPhone and iPad" },
  { id: "android", label: "Android" },
  { id: "web", label: "In a web browser" },
];

/** Exactly what the client posts. Deliberately small and all-primitive, so it is cheap to validate. */
export type QuoteSelections = {
  goal: QuoteGoal | null;
  buildTypes: QuoteBuildType[];
  websiteTier: EstimatorWebsiteTier;
  features: QuoteFeatureKey[];
  platforms: ("ios" | "android" | "web")[];
  clarity: EstimatorComplexity;
};

export const DEFAULT_SELECTIONS: QuoteSelections = {
  goal: null,
  buildTypes: [],
  websiteTier: "sells",
  features: [],
  platforms: ["web"],
  clarity: "moderate",
};

const BUILD_TYPE_IDS = new Set<string>(BUILD_TYPES.map((b) => b.id));
const FEATURE_IDS = new Set<string>(FEATURES.map((f) => f.id));
const PLATFORM_IDS = new Set<string>(APP_PLATFORMS.map((p) => p.id));
const WEBSITE_TIER_IDS = new Set<string>(WEBSITE_TIERS.map((t) => t.id));
const CLARITY_IDS = new Set<string>(SPEC_CLARITY.map((c) => c.id));
const GOAL_IDS = new Set<string>(QUOTE_GOALS.map((g) => g.id));

function pickStrings(value: unknown, allowed: Set<string>, cap: number): string[] {
  if (!Array.isArray(value)) return [];
  const seen: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") continue;
    if (!allowed.has(item)) continue;
    if (seen.includes(item)) continue;
    seen.push(item);
    if (seen.length >= cap) break;
  }
  return seen;
}

function pickOne<T extends string>(value: unknown, allowed: Set<string>, fallback: T): T {
  return typeof value === "string" && allowed.has(value) ? (value as T) : fallback;
}

/**
 * Coerce an untrusted request body into a `QuoteSelections`.
 *
 * Anything unrecognised is dropped rather than rejected. This runs on a public endpoint, and the
 * worst case for an unknown option is a quote priced off the options we did understand, which is
 * still a usable lead. A hard rejection would instead lose the enquiry entirely because someone had
 * a stale tab open across a deploy.
 */
export function parseQuoteSelections(value: unknown): QuoteSelections {
  const raw = (value ?? {}) as Record<string, unknown>;
  return {
    goal:
      typeof raw.goal === "string" && GOAL_IDS.has(raw.goal) ? (raw.goal as QuoteGoal) : null,
    buildTypes: pickStrings(raw.buildTypes, BUILD_TYPE_IDS, BUILD_TYPES.length) as QuoteBuildType[],
    websiteTier: pickOne(raw.websiteTier, WEBSITE_TIER_IDS, DEFAULT_SELECTIONS.websiteTier),
    features: pickStrings(raw.features, FEATURE_IDS, FEATURES.length) as QuoteFeatureKey[],
    platforms: pickStrings(raw.platforms, PLATFORM_IDS, APP_PLATFORMS.length) as (
      | "ios"
      | "android"
      | "web"
    )[],
    clarity: pickOne(raw.clarity, CLARITY_IDS, DEFAULT_SELECTIONS.clarity),
  };
}

/** True once there is enough to price. Nothing ticked means no quote, not a cheap quote. */
export function hasPriceableScope(sel: QuoteSelections): boolean {
  return sel.buildTypes.length > 0;
}

function has(sel: QuoteSelections, key: QuoteFeatureKey): boolean {
  return sel.features.includes(key);
}

/** Is a feature tick box relevant to what they have already said they want? */
export function isFeatureOffered(sel: QuoteSelections, key: QuoteFeatureKey): boolean {
  const feature = FEATURES.find((f) => f.id === key);
  if (!feature) return false;
  if (feature.requires && !has(sel, feature.requires)) return false;
  if (feature.buildTypes && !feature.buildTypes.some((b) => sel.buildTypes.includes(b))) {
    return false;
  }
  return true;
}

/**
 * Drop ticks that are no longer reachable.
 *
 * Someone can tick "online bookings", tick the subscriptions sub-option, then untick bookings. The
 * sub-tick is then invisible but still in state, and would quietly add thousands to the quote for
 * something the person can no longer see they asked for. Run this before pricing.
 */
export function pruneSelections(sel: QuoteSelections): QuoteSelections {
  let features = sel.features;
  // Two passes: a sub-option can depend on another sub-option, so one pass could leave an orphan.
  for (let i = 0; i < 2; i++) {
    features = features.filter((key) => isFeatureOffered({ ...sel, features }, key));
  }
  return {
    ...sel,
    features,
    platforms: sel.buildTypes.includes("app") ? sel.platforms : [],
  };
}

function bookingPayments(sel: QuoteSelections): EstimatorBookingPayments {
  if (!has(sel, "bookingPayments")) return "none";
  return has(sel, "bookingPaymentsAdvanced") ? "complex" : "simple";
}

function integrations(sel: QuoteSelections): EstimatorIntegrations {
  if (!has(sel, "integrations")) return "none";
  return has(sel, "integrationsHeavy") ? "heavy" : "some";
}

function ecommerce(sel: QuoteSelections): EstimatorEcommerce {
  if (!sel.buildTypes.includes("ecommerce")) return "none";
  return has(sel, "largeCatalogue") ? "advanced" : "standard";
}

/**
 * Translate ticks into the estimator's input shape.
 *
 * The estimator models a single `primary` service with an optional `combination` breakdown, which
 * predates this screen. Ticking one box maps straight onto that primary; ticking several maps onto
 * "combination", where the estimator applies its multi-surface overhead. A shop rides the website
 * stream in that model, so ticking both "a website" and "an online shop" is one surface, not two,
 * which is correct: it is one site that happens to sell.
 */
export function selectionsToEstimatorInputs(input: QuoteSelections): ProjectEstimatorInputs {
  const sel = pruneSelections(input);
  const wantsWebsite = sel.buildTypes.includes("website") || sel.buildTypes.includes("ecommerce");
  const wantsSystem = sel.buildTypes.includes("system");
  const wantsApp = sel.buildTypes.includes("app");
  const surfaces = [wantsWebsite, wantsSystem, wantsApp].filter(Boolean).length;

  let primary: ProjectEstimatorInputs["primary"];
  if (surfaces > 1) {
    primary = "combination";
  } else if (wantsSystem) {
    primary = "system";
  } else if (wantsApp) {
    primary = "app";
  } else if (sel.buildTypes.includes("ecommerce")) {
    primary = "ecommerce";
  } else {
    primary = "website";
  }

  return {
    primary,
    combination: { website: wantsWebsite, system: wantsSystem, app: wantsApp },
    websiteTier: sel.websiteTier,
    ecommerce: ecommerce(sel),
    bookingPayments: bookingPayments(sel),
    userAccounts: has(sel, "userAccounts"),
    adminDashboard: has(sel, "adminDashboard"),
    integrations: integrations(sel),
    platformIos: sel.platforms.includes("ios"),
    platformAndroid: sel.platforms.includes("android"),
    platformWeb: sel.platforms.includes("web"),
    complexity: sel.clarity,
  };
}

/**
 * Which monthly retainer this build lands on.
 *
 * Mirrors the Starter / Growth / Operator product definition in Lead Engine's staff hub:
 *   Starter  — a website, straightforwardly.
 *   Growth   — a website plus the booking and enquiry journey around it.
 *   Operator — a bespoke system: apps, portals, dashboards, accounts, integrations.
 *
 * Derived rather than asked. The tier follows from the size of what gets built, so making someone
 * choose it would be asking a question we already know the answer to, and inviting a wrong answer.
 */
export type RetainerSignals = {
  system: boolean;
  app: boolean;
  ecommerce: boolean;
  userAccounts: boolean;
  adminDashboard: boolean;
  integrations: boolean;
  bookingPayments: boolean;
};

/**
 * The tier rule itself, in one place.
 *
 * Two forms ask for the same build in different words (the quote builder and the estimator), so the
 * rule takes plain signals rather than either form's shape. Both feed it. If it lived in only one of
 * them, the other would grow a second copy and the two would quietly start quoting different plans
 * for the same build.
 */
export function retainerTierFromSignals(signals: RetainerSignals): RetainerTier {
  /* An app with accounts and an admin side behind it is the Platform plan: more moving parts to
     keep running than a single internal tool. Matches CARE_PLAN_FOR_BUILD in lead-engine, where an
     app or web platform suggests £750. */
  if (signals.app && (signals.userAccounts || signals.adminDashboard || signals.integrations)) {
    return "platform";
  }

  const operator =
    signals.system ||
    signals.app ||
    signals.userAccounts ||
    signals.adminDashboard ||
    signals.integrations;
  if (operator) return "operator";

  if (signals.ecommerce || signals.bookingPayments) return "growth";

  return "starter";
}

export function retainerTierFor(input: QuoteSelections): RetainerTier {
  const sel = pruneSelections(input);
  return retainerTierFromSignals({
    system: sel.buildTypes.includes("system"),
    app: sel.buildTypes.includes("app"),
    ecommerce: sel.buildTypes.includes("ecommerce"),
    userAccounts: has(sel, "userAccounts"),
    adminDashboard: has(sel, "adminDashboard"),
    integrations: has(sel, "integrations"),
    bookingPayments: has(sel, "bookingPayments"),
  });
}

/** The retainer as a figure and a label, ready to print. */
export function retainerFor(input: QuoteSelections) {
  const tier = retainerTierFor(input);
  return { tier, ...RETAINER_TIERS[tier] };
}

/** What they said they were trying to fix, phrased so it can be read back to them. */
export function goalReflection(input: QuoteSelections): string | null {
  if (!input.goal) return null;
  return QUOTE_GOALS.find((g) => g.id === input.goal)?.reflect ?? null;
}

export type QuoteLineItem = { label: string; detail: string };

/**
 * The "what this covers" list for the quote email and the on-screen result.
 *
 * Built from the same labels the person ticked, so the email reads back their own choices rather
 * than a translation of them into our internal vocabulary.
 */
export function quoteLineItems(input: QuoteSelections): QuoteLineItem[] {
  const sel = pruneSelections(input);
  const items: QuoteLineItem[] = [];

  for (const build of BUILD_TYPES) {
    if (!sel.buildTypes.includes(build.id)) continue;
    if (build.id === "website") {
      const tier = WEBSITE_TIERS.find((t) => t.id === sel.websiteTier);
      items.push({
        label: build.label,
        detail: tier ? tier.detail : build.detail,
      });
      continue;
    }
    if (build.id === "app") {
      const platforms = APP_PLATFORMS.filter((p) => sel.platforms.includes(p.id)).map((p) => p.label);
      items.push({
        label: build.label,
        detail: platforms.length ? `Running on: ${platforms.join(", ")}.` : build.detail,
      });
      continue;
    }
    items.push({ label: build.label, detail: build.detail });
  }

  for (const feature of FEATURES) {
    if (!sel.features.includes(feature.id)) continue;
    items.push({ label: feature.label, detail: feature.detail });
  }

  // The retainer is deliberately not a line item here. It is a recurring monthly cost, and listing
  // it beside one-off build items is how someone reads £500 a month as £500 once.
  return items;
}

/** One-line summary of the scope, for the lead notes a rep reads before calling. */
export function quoteScopeSummary(input: QuoteSelections): string {
  const sel = pruneSelections(input);
  const builds = BUILD_TYPES.filter((b) => sel.buildTypes.includes(b.id)).map((b) => b.label);
  if (!builds.length) return "No scope selected";
  return builds.join(" plus ");
}
